import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Grid, IconButton, Tooltip } from '@mui/material';
import { connect, useDispatch } from 'react-redux';
import { If } from '../../common/IfComponent';
import { CostDto } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { toPriceString } from '../../utils';
import { showAlertDialog, showConfirmDialog } from '../ui/redux/uiSlices';
import { getCostType } from './calculationTypes';
import { getSelectedCalculationPricelistCurrency } from './redux/calculationSelectors';
import { deleteCost, showEditCostDialog } from './redux/calculationSlices';

type OwnProps = {
    cost: CostDto;
};

const mapStateToProps = (state: RootState, { cost }: OwnProps) => ({
    cost: cost,
    currentCalculation: state.calculation.calculation,
    selectedCurrency: getSelectedCalculationPricelistCurrency(state)
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const CostTypeItem = ({ cost, currentCalculation, selectedCurrency }: Props) => {
    const dispatch = useDispatch();

    const setShowDeleteDialog = (costId: number) => {
        dispatch(
            showConfirmDialog({
                dialogMessage: 'Do you want do remove this cost?',
                dialogTitle: 'Remove cost',
                okAction: deleteCost.request({ costId }),
                primaryActionName: 'Remove'
            })
        );
    };

    const getDeleteIcon = () => {
        return (
            <IconButton sx={{ padding: '0px' }} onClick={() => setShowDeleteDialog(cost.costId)}>
                <Tooltip title="Remove cost">
                    <DeleteIcon sx={{ float: 'right', fill: 'red' }} />
                </Tooltip>
            </IconButton>
        );
    };

    const getWarningIcon = () => {
        return (
            <IconButton>
                <Tooltip title={cost.inheritedFrom?.inheritedFromPricelistName ?? ''}>
                    <WarningIcon
                        sx={{
                            fill: '#f5c842'
                        }}
                    />
                </Tooltip>
            </IconButton>
        );
    };

    const canEdit = () => {
        const currentCostGroup = currentCalculation.costGroups.find(c => c.costGroupId === cost.costGroupId);
        // We can edit the cost if it's inherrited and overridden
        if (currentCostGroup?.inheritedFrom != null) {
            return currentCostGroup?.overridesCostGroupId != null;
        }
        // if it's not inherited we can always edit the cost.
        return true;
    };

    const showEditDialog = () => {
        if (currentCalculation.inheritedFrom == null) {
            dispatch(showEditCostDialog(cost));
        } else if (currentCalculation.costGroups.map(c => c.costGroupId).indexOf(cost.costGroupId) >= 0) {
            dispatch(showEditCostDialog(cost));
        } else {
            dispatch(
                showAlertDialog({
                    dialogTitle: 'Warning!',
                    dialogMessage: 'You need to first override the costgroup in order to override the cost.',
                    icon: 'warning'
                })
            );
        }
    };

    return (
        <>
            <Grid item xs sx={{ marginLeft: '8px', marginTop: '3px' }}>
                <strong>{cost.name}</strong>
            </Grid>
            <Grid item>
                <Box component="span">
                    <If
                        condition={canEdit()}
                        then={
                            <IconButton sx={{ padding: '0px' }} onClick={showEditDialog}>
                                <Tooltip title="Edit">
                                    <CreateIcon />
                                </Tooltip>
                            </IconButton>
                        }
                        else={null}
                    />
                    {(cost.inheritedFrom == null || cost.overridesCostId != null) && getDeleteIcon()}
                    <If
                        condition={cost.inheritedFrom != null && cost.overridesCostId == null}
                        then={getWarningIcon()}
                        else={null}
                    />
                </Box>
            </Grid>
            <Grid container direction="column">
                <Grid item>
                    <Box sx={{ display: 'block', marginLeft: '8px' }}>
                        {toPriceString(cost.amount)} {getCostType(selectedCurrency, cost.costType)}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default connect(mapStateToProps)(CostTypeItem);
